import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import EmailSettings from '../../components/settings/EmailSettings';
import { User } from '../../types';
import './SettingsPage.css';

const SettingsPage: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<string>('email');

  // Check for auth token in URL and restore authentication
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const authToken = urlParams.get('auth_token');
    
    if (authToken) {
      // Restore the auth token
      localStorage.setItem('token', authToken);
      
      // Remove the auth token from the URL
      const newUrl = window.location.pathname + 
        window.location.search.replace(/[&?]auth_token=[^&]+/, '');
      window.history.replaceState({}, document.title, newUrl);
      
      // Reload the page to restore authentication
      window.location.reload();
    }
  }, []);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'email':
        return <EmailSettings />;
      case 'account':
        return (
          <div className="account-settings">
            <h2>Account Settings</h2>
            <form className="settings-form" onSubmit={(e) => {
              e.preventDefault();
              
              // Get form values
              const firstName = (document.getElementById('firstName') as HTMLInputElement).value;
              const lastName = (document.getElementById('lastName') as HTMLInputElement).value;
              const school = (document.getElementById('school') as HTMLInputElement).value;
              const grade = (document.getElementById('grade') as HTMLSelectElement).value;
              
              // Save to localStorage
              localStorage.setItem('userName', `${firstName} ${lastName}`.trim());
              localStorage.setItem('userSchool', school);
              localStorage.setItem('userGrade', grade);
              
              // Show success message
              alert('Your information has been saved successfully!');
            }}>
              <div className="mb-3">
                <label htmlFor="email" className="form-label">Email Address</label>
                <input 
                  type="email" 
                  className="form-control" 
                  id="email" 
                  value={user?.email || ''} 
                  disabled 
                />
                <div className="form-text">Your email address cannot be changed.</div>
              </div>
              
              <div className="mb-3">
                <label htmlFor="firstName" className="form-label">First Name</label>
                <input 
                  type="text" 
                  className="form-control" 
                  id="firstName" 
                  defaultValue={user?.firstName || ''} 
                />
              </div>
              
              <div className="mb-3">
                <label htmlFor="lastName" className="form-label">Last Name</label>
                <input 
                  type="text" 
                  className="form-control" 
                  id="lastName" 
                  defaultValue={user?.lastName || ''} 
                />
              </div>
              
              <div className="mb-3">
                <label htmlFor="school" className="form-label">School</label>
                <input 
                  type="text" 
                  className="form-control" 
                  id="school" 
                  defaultValue={user?.school || ''} 
                />
              </div>
              
              <div className="mb-3">
                <label htmlFor="grade" className="form-label">Grade Level</label>
                <select 
                  className="form-select" 
                  id="grade" 
                  defaultValue={user?.grade || ''}
                >
                  <option value="">Select your grade/year</option>
                  <option value="High School Freshman">High School Freshman</option>
                  <option value="High School Sophomore">High School Sophomore</option>
                  <option value="High School Junior">High School Junior</option>
                  <option value="High School Senior">High School Senior</option>
                </select>
              </div>
              
              <div className="mb-3">
                <label htmlFor="achievements" className="form-label">Achievements</label>
                <textarea 
                  className="form-control" 
                  id="achievements" 
                  rows={3} 
                  placeholder="List your achievements, one per line (e.g., Science Fair Winner, Math Olympiad)"
                  defaultValue={localStorage.getItem('userAchievements')?.replace(/,/g, '\n') || ''}
                  onChange={(e) => {
                    // Store achievements in localStorage
                    const achievements = e.target.value.split('\n')
                      .filter(line => line.trim() !== '')
                      .join(',');
                    localStorage.setItem('userAchievements', achievements);
                  }}
                ></textarea>
                <div className="form-text">List your academic achievements, awards, or relevant experiences.</div>
              </div>
              
              <div className="mb-3">
                <label htmlFor="interests" className="form-label">Academic Interests</label>
                <input 
                  type="text" 
                  className="form-control" 
                  id="interests" 
                  placeholder="e.g., Artificial Intelligence, Biology, Chemistry"
                  defaultValue={localStorage.getItem('userInterests') || ''}
                  onChange={(e) => localStorage.setItem('userInterests', e.target.value)}
                />
                <div className="form-text">Enter your academic interests, separated by commas.</div>
              </div>
              
              <button type="submit" className="btn btn-primary">Save Changes</button>
            </form>
          </div>
        );
      case 'password':
        return (
          <div className="password-settings">
            <h2>Change Password</h2>
            <form className="settings-form">
              <div className="mb-3">
                <label htmlFor="currentPassword" className="form-label">Current Password</label>
                <input 
                  type="password" 
                  className="form-control" 
                  id="currentPassword" 
                />
              </div>
              
              <div className="mb-3">
                <label htmlFor="newPassword" className="form-label">New Password</label>
                <input 
                  type="password" 
                  className="form-control" 
                  id="newPassword" 
                />
                <div className="form-text">Password must be at least 8 characters long.</div>
              </div>
              
              <div className="mb-3">
                <label htmlFor="confirmPassword" className="form-label">Confirm New Password</label>
                <input 
                  type="password" 
                  className="form-control" 
                  id="confirmPassword" 
                />
              </div>
              
              <button type="submit" className="btn btn-primary">Change Password</button>
            </form>
          </div>
        );
      case 'notifications':
        return (
          <div className="notification-settings">
            <h2>Notification Settings</h2>
            <div className="settings-form">
              <div className="mb-3">
                <div className="form-check form-switch">
                  <input className="form-check-input" type="checkbox" id="emailNotifications" defaultChecked />
                  <label className="form-check-label" htmlFor="emailNotifications">
                    Email Notifications
                  </label>
                </div>
                <div className="form-text">Receive email notifications when professors respond to your messages.</div>
              </div>
              
              <div className="mb-3">
                <div className="form-check form-switch">
                  <input className="form-check-input" type="checkbox" id="weeklyDigest" defaultChecked />
                  <label className="form-check-label" htmlFor="weeklyDigest">
                    Weekly Digest
                  </label>
                </div>
                <div className="form-text">Receive a weekly summary of your outreach activities.</div>
              </div>
              
              <button type="submit" className="btn btn-primary">Save Preferences</button>
            </div>
          </div>
        );
      default:
        return <div>Select a tab</div>;
    }
  };

  return (
    <div className="settings-page">
      <div className="container py-5">
        <div className="row">
          <div className="col-12">
            <h1 className="mb-4">Settings</h1>
          </div>
        </div>
        
        <div className="row">
          <div className="col-md-3">
            <div className="settings-sidebar">
              <div className="list-group">
                <button 
                  className={`list-group-item list-group-item-action ${activeTab === 'email' ? 'active' : ''}`}
                  onClick={() => setActiveTab('email')}
                >
                  <i className="bi bi-envelope me-2"></i>
                  Email Settings
                </button>
                <button 
                  className={`list-group-item list-group-item-action ${activeTab === 'account' ? 'active' : ''}`}
                  onClick={() => setActiveTab('account')}
                >
                  <i className="bi bi-person me-2"></i>
                  Account Information
                </button>
                <button 
                  className={`list-group-item list-group-item-action ${activeTab === 'password' ? 'active' : ''}`}
                  onClick={() => setActiveTab('password')}
                >
                  <i className="bi bi-key me-2"></i>
                  Change Password
                </button>
                <button 
                  className={`list-group-item list-group-item-action ${activeTab === 'notifications' ? 'active' : ''}`}
                  onClick={() => setActiveTab('notifications')}
                >
                  <i className="bi bi-bell me-2"></i>
                  Notifications
                </button>
              </div>
            </div>
          </div>
          
          <div className="col-md-9">
            <div className="settings-content">
              {renderTabContent()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
